// src/services/deviceTier.ts
//
// 8章: ブラウザ/デバイス対応表に基づく3段階のTier判定。
// Tier 1: WebGPU利用可 → オンデバイスLLM(WebLLM)+ PoseLandmarker GPUデリゲート
// Tier 2: WebGPU不可だがWASM SIMD対応 → PoseLandmarker CPUデリゲート + テンプレート助言
// Tier 3: 上記いずれも不可 → 低解像度・低フレームレートに縮退 + テンプレート助言
//
// 判定は起動時に1度だけ行い、結果をセッション中キャッシュする(頻繁な再判定は
// バッテリー消費・レイテンシの無駄になるため)。

export type Tier = 1 | 2 | 3;

export interface TierInfo {
  tier: Tier;
  hasWebGpu: boolean;
  hasWasmSimd: boolean;
  reason: string;
}

async function detectWebGpu(): Promise<boolean> {
  if (!("gpu" in navigator)) return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gpu = (navigator as any).gpu;
    const adapter = await gpu?.requestAdapter?.();
    return !!adapter;
  } catch {
    return false;
  }
}

function detectWasmSimd(): boolean {
  // WASM SIMDの実行時検出は既知のバイトコード片を試験的にインスタンス化して判定する
  // (广く使われる手法。失敗時はSIMD非対応とみなす)。
  try {
    // prettier-ignore
    const simdTestBytes = new Uint8Array([
      0,97,115,109,1,0,0,0,1,5,1,96,0,1,123,3,2,1,0,10,10,1,8,0,65,0,253,15,253,98,11,
    ]);
    return WebAssembly.validate(simdTestBytes);
  } catch {
    return false;
  }
}

export async function detectDeviceTier(): Promise<TierInfo> {
  const hasWebGpu = await detectWebGpu();
  const hasWasmSimd = detectWasmSimd();

  if (hasWebGpu && hasWasmSimd) {
    return {
      tier: 1,
      hasWebGpu,
      hasWasmSimd,
      reason: "WebGPU + WASM SIMD 両対応",
    };
  }
  if (hasWasmSimd) {
    return {
      tier: 2,
      hasWebGpu,
      hasWasmSimd,
      reason: "WASM SIMDのみ対応(WebGPU不可)",
    };
  }
  return {
    tier: 3,
    hasWebGpu,
    hasWasmSimd,
    reason: "WebGPU・WASM SIMDともに不可。低解像度モードへ縮退",
  };
}

/** Tierごとの推奨カメラ制約(8章の性能プロファイル) */
export function recommendedCameraConstraints(tier: Tier): MediaTrackConstraints {
  switch (tier) {
    case 1:
      return { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
    case 2:
      return { width: { ideal: 960 }, height: { ideal: 540 }, frameRate: { ideal: 24 } };
    case 3:
      return { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 15 } };
  }
}
